<template>
    <div ref="observerRef">
        <section v-if="displayedPromos && displayedPromos.length > 0 && inView" class="o-flexible-module">
            <div class="c-promo-slider has-reduced-height js-promo-slider" :data-component-id="componentId">
                <div class="grid-container">
                    <div class="grid-x grid-margin-x">
                        <div class="small-12 cell">
                            <div class="c-promo-slider__slider-wrapper s-swiper">
                                <div class="c-promo-slider__items">
                                    <swiper-container
                                        v-if="displayedPromos.length > 0"
                                        ref="containerRef"
                                        :auto-height="true"
                                        :autoplay-delay="7000"
                                        :autoplay-pause-on-mouse-enter="true"
                                        :loop="true"
                                        :slides-per-view="1"
                                        :space-between="40">
                                        <swiper-slide v-for="(promo, index) in displayedPromos" :key="index">
                                            <div
                                                class="c-promo-slider__item"
                                                :class="[
                                                    `t-${promo.settings.theme}`,
                                                    { 'has-media-right': promo.settings.media_placement === 'right' },
                                                ]">
                                                <div class="grid-x grid-margin-x align-middle align-center">
                                                    <div
                                                        class="small-12 medium-6 cell"
                                                        :class="[
                                                            promo.settings.media_placement === 'right'
                                                                ? 'medium-order-2 medium-offset-1'
                                                                : '',
                                                        ]">
                                                        <div
                                                            class="c-promo-slider__item-media"
                                                            :class="[{ 'is-desktop': promo.mobile_image }]"
                                                            v-html="promo?.image?.attachments['large']"></div>

                                                        <div
                                                            v-if="promo.mobile_image"
                                                            class="c-promo-slider__item-media is-mobile"
                                                            v-html="promo?.mobile_image?.attachments['large']"></div>
                                                    </div>

                                                    <div
                                                        class="small-12 medium-5 cell"
                                                        :class="[
                                                            promo.settings.media_placement === 'right'
                                                                ? 'medium-order-1'
                                                                : 'medium-offset-1',
                                                        ]">
                                                        <div class="c-promo-slider__item-content-wrapper">
                                                            <template v-if="promo.title">
                                                                <h2
                                                                    v-if="promo.settings.heading_level === 'h2'"
                                                                    class="c-promo-slider__item-title"
                                                                    v-html="promo.title"></h2>
                                                                <h3
                                                                    v-else-if="promo.settings.heading_level === 'h3'"
                                                                    class="c-promo-slider__item-title"
                                                                    v-html="promo.title"></h3>
                                                                <div
                                                                    v-else
                                                                    class="c-promo-slider__item-title"
                                                                    v-html="promo.title"></div>
                                                            </template>

                                                            <div
                                                                v-if="promo.content"
                                                                class="c-promo-slider__item-content s-wp-cms"
                                                                v-html="promo.content"></div>

                                                            <NuxtLink
                                                                v-if="promo.link"
                                                                :to="promo.link.url"
                                                                :target="promo.link.target"
                                                                class="c-promo-slider__item-link"
                                                                no-prefetch>
                                                                <span v-html="promo.link.title"></span>
                                                            </NuxtLink>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </swiper-slide>
                                    </swiper-container>

                                    <div
                                        v-if="displayedPromos.length > 1"
                                        class="swiper-pagination js-swiper-pagination"></div>

                                    <swiper-navigation
                                        v-if="displayedPromos.length > 1 && swiper"
                                        class="swiper-navigation">
                                        <button
                                            @click="swiper.prev()"
                                            class="swiper-button-prev js-swiper-button-prev"
                                            aria-label="Previous slide"
                                            type="button">
                                            <i class="fa-sharp fa-chevron-left"></i>
                                        </button>

                                        <button
                                            @click="swiper.next()"
                                            class="swiper-button-next js-swiper-button-next"
                                            aria-label="Next slide"
                                            type="button">
                                            <i class="fa-sharp fa-chevron-right"></i>
                                        </button>
                                    </swiper-navigation>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>
    import moment from "moment";

    const props = defineProps({
        home: {
            type: Object,
            required: true,
        },
    });

    const componentId = useId();
    const containerRef = ref(null);
    const swiper = useSwiper(containerRef);
    const displayedPromos = ref(null);
    const inView = ref(false);
    const observerRef = ref(null);
    let apiCalled = false;

    useLazyObserver(observerRef, () => {
        if (!apiCalled) {
            apiCalled = true;
            inView.value = true;
            getPromos();
        }
    });

    const getHomePromos = async (homePromos) => {
        const promos = [];
        const allowedIds = homePromos.map((p) => p.ID);

        const { data } = await useFetch(`/api/wp/promos/promo`);

        // console.log("allowedIds: ", allowedIds);

        if (data) {
            const filtered = data.value.filter((item) => allowedIds.includes(item.id));

            // console.log("All Promos: ", data.value);

            filtered.forEach((item) => {
                promos.push({
                    ...item.acf,
                    ...(item.mobile_image && { mobile_image: item.mobile_image }),
                });
            });
        }

        // console.log("getHomePromos() promos: ", promos);

        return promos;
    };

    const getPromos = async () => {
        let promos = [];
        const currentDate = moment();

        promos = await getHomePromos(props.home.promos);

        displayedPromos.value = promos.filter((promo) => {
            const format = "DD/MM/YYYY";
            let passesDate = true;

            if (promo.settings.start_date) {
                passesDate &&= moment(promo.settings.start_date, format).isSameOrBefore(currentDate);
            }

            if (promo.settings.end_date) {
                passesDate &&= moment(promo.settings.end_date, format).isSameOrAfter(currentDate);
            }

            return passesDate;
        });
    };

    // console.log("Home: ", props.home);
    // console.log("displayedPromos: ", displayedPromos.value);
</script>
